import React from 'react';
import { useSelector } from 'react-redux';

import {
  isServiceAccountsEnabled,
  useServiceAccountRevokeApiToken,
  useServiceAccounts,
  useServiceAccountsTokens,
} from '@neptune/service-accounts-business-logic';
import type {
  ServiceAccount,
  ServiceAccountReactivateData,
} from '@neptune/service-accounts-domain';
import {
  CreateServiceAccountTile,
  ServiceAccountsPlaceholder,
  WorkspaceEmptyState,
  WorkspaceServiceAccountsList,
} from '@neptune/service-accounts-ui';
import { useGenericModal } from '@neptune/shared/common-business-logic';
import { ErrorPlaceholderContainer } from '@neptune/shared/common-feature';
import { useLocalModal } from '@neptune/shared/common-util';
import { OrganizationWithRole } from '@neptune/shared/core-organizations-domain';
import { Layout } from '@neptune/shared/venus-ui';

import LoadingIndicator from 'components/loading-indicator/LoadingIndicator';
import { getCurrentOrganization } from 'state/selectors';

import {
  DEACTIVATE_SERVICE_ACCOUNT_MODAL_NAME,
  DeactivateServiceAccountModalContainer,
} from './DeactivateServiceAccountModalContainer';
import {
  REACTIVATE_SERVICE_ACCOUNT_MODAL_NAME,
  ReactivateServiceAccountModalContainer,
} from './ReactivateServiceAccountModalContainer';
import { ResetServiceAccountTokenModalContainer } from './ResetServiceAccountTokenModalContainer';
import { WorkspaceCreateServiceAccountModalContainer } from './WorkspaceCreateServiceAccountModalContainer';

export const WorkspaceServiceAccounts = () => {
  const organization: OrganizationWithRole | undefined = useSelector(getCurrentOrganization);
  const organizationName = organization?.name;
  const { value: serviceAccounts, loading, error, refresh } = useServiceAccounts(organizationName);
  const {
    isOpen: isCreateServiceAccountModalOpen,
    open: requestCreateNewServiceAccount,
    close: closeCreateServiceAccountModal,
  } = useLocalModal();
  const { tokensMap, toggleApiToken, getApiToken } = useServiceAccountsTokens({ organizationName });

  const { openWithData: requestDeactivateServiceAccount } = useGenericModal<ServiceAccount>(
    DEACTIVATE_SERVICE_ACCOUNT_MODAL_NAME,
  );

  const { openWithData: requestReactivateServiceAccount } =
    useGenericModal<ServiceAccountReactivateData>(REACTIVATE_SERVICE_ACCOUNT_MODAL_NAME);

  const onRevoke = React.useCallback(
    async (serviceAccountId: string) => {
      await getApiToken(serviceAccountId);
      refresh();
    },
    [getApiToken, refresh],
  );

  const { openWithData: requestRevokeServiceAccount } = useServiceAccountRevokeApiToken({
    organizationName,
  });

  const deactivateServiceAccount = React.useCallback(
    (serviceAccount: ServiceAccount) => {
      requestDeactivateServiceAccount(serviceAccount);
    },
    [requestDeactivateServiceAccount],
  );

  const reactivateServiceAccount = React.useCallback(
    (serviceAccount: ServiceAccount) => {
      requestReactivateServiceAccount(serviceAccount);
    },
    [requestReactivateServiceAccount],
  );

  if (!organization) {
    return null;
  }

  if (!isServiceAccountsEnabled(organization.traits)) {
    return <ServiceAccountsPlaceholder />;
  }

  if (error) {
    return <ErrorPlaceholderContainer context="service-accounts" />;
  }

  return (
    <Layout.Column data-role="service-accounts">
      <WorkspaceServiceAccountsList
        tokenStatus={tokensMap}
        loading={loading}
        entries={serviceAccounts}
        createNewElement={<CreateServiceAccountTile onCreate={requestCreateNewServiceAccount} />}
        emptyState={<WorkspaceEmptyState onNewServiceAccount={requestCreateNewServiceAccount} />}
        onDeactivateAccount={deactivateServiceAccount}
        onReactivateAccount={reactivateServiceAccount}
        onToggleApiToken={toggleApiToken}
        getApiToken={getApiToken}
        onResetApiToken={requestRevokeServiceAccount}
      />
      <LoadingIndicator isVisible={loading} centered />
      <WorkspaceCreateServiceAccountModalContainer
        isOpen={isCreateServiceAccountModalOpen}
        onClose={closeCreateServiceAccountModal}
        onSubmit={refresh}
      />
      <DeactivateServiceAccountModalContainer onSubmit={refresh} />
      <ReactivateServiceAccountModalContainer onSubmit={refresh} />
      <ResetServiceAccountTokenModalContainer onRevoke={onRevoke} />
    </Layout.Column>
  );
};
